import { decode as decodeBlurHash } from 'blurhash'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { t } from '../../../../../common/i18n.js'
import type { Language } from '../../../../../common/types/i18n.js'
import type { NotionColor } from '../../../../../common/types/notion.js'
import { isEventPast, localYmd } from '../../../../../common/utils/event-date-utils.js'
import {
    Calendar,
    Check,
    Copy,
    ExternalLink,
    MapPin,
    SquareArrowRight,
    X,
} from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { Tabs } from '../../../../components/tabs/tabs.js'
import { formatWeekday, localeOf } from '../../utils/format.js'
import './events-view.css'
import { flattenNotionColumns, resolveNotionHref } from './notion-block-utils.js'

interface NotionEventItem {
    id: string
    name: string
    icon: string | null
    dateStart: string | null
    dateEnd: string | null
    posterUrl: string | null
    place: string | null
    placeColor: NotionColor | null
    category: string | null
    categoryColor: NotionColor | null
    blurHash: string | null
    notionUrl: string | null
}

// Tiny decoded canvas size — BlurHash is gradient-y so 32×18 is plenty; the
// browser scales it up smoothly to the poster size.
const BLUR_CANVAS_WIDTH = 32
const BLUR_CANVAS_HEIGHT = 18

const BlurHashCanvas = ({ hash }: { hash: string }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        try {
            const pixels = decodeBlurHash(hash, BLUR_CANVAS_WIDTH, BLUR_CANVAS_HEIGHT)
            const ctx = canvas.getContext('2d')
            if (!ctx) return
            const imageData = ctx.createImageData(BLUR_CANVAS_WIDTH, BLUR_CANVAS_HEIGHT)
            imageData.data.set(pixels)
            ctx.putImageData(imageData, 0, 0)
        } catch {
            // Swallow: a bad hash should not break the list.
        }
    }, [hash])

    return (
        <canvas
            ref={canvasRef}
            width={BLUR_CANVAS_WIDTH}
            height={BLUR_CANVAS_HEIGHT}
            className="portal-event-card__poster-blur"
            aria-hidden="true"
        />
    )
}

const EventPoster = ({ src, alt, hash }: { src: string; alt: string; hash: string | null }) => {
    const [loaded, setLoaded] = useState(false)
    return (
        <div className="portal-event-card__poster-wrap">
            {hash && <BlurHashCanvas hash={hash} />}
            <img
                src={src}
                alt={alt}
                className="portal-event-card__poster"
                loading="lazy"
                data-loaded={loaded || undefined}
                onLoad={() => setLoaded(true)}
            />
        </div>
    )
}

interface NotionRichTextElement {
    plain_text?: string
    href?: string | null
    annotations?: {
        bold?: boolean
        italic?: boolean
        strikethrough?: boolean
        underline?: boolean
        code?: boolean
        color?: string
    }
}

interface NotionBlockContent {
    rich_text?: NotionRichTextElement[]
    icon?: { emoji?: string } | null
}

interface NotionBlock {
    id: string
    type: string
    has_children?: boolean
    children?: NotionBlock[]
    [key: string]: NotionBlockContent | NotionBlock[] | string | boolean | undefined
}

/** The block's own content payload (under the key named after its type), or
 *  null when it has none / it's a structural field like `children`. */
const blockContent = (block: NotionBlock): NotionBlockContent | null => {
    const content = block[block.type]
    if (!content || typeof content !== 'object' || Array.isArray(content)) return null
    return content
}

type EventFilter = 'all' | 'upcoming'

const pad2 = (n: number): string => String(n).padStart(2, '0')

const toDdMm = (iso: string): string => {
    const d = new Date(iso)
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`
}

const NotionBlockRenderer = ({ blocks }: { blocks: NotionBlock[] }) => {
    // Columns are flattened into one stream (the modal has its own poster
    // split), then leading empty blocks are trimmed to avoid unwanted gaps.
    const trimmedBlocks = flattenNotionColumns(blocks)
    while (trimmedBlocks.length > 0) {
        const block = trimmedBlocks[0]
        const type = block.type
        const content = blockContent(block)

        let isEmpty = false
        if (content) {
            if (type === 'paragraph' || type.startsWith('heading') || type.endsWith('list_item')) {
                const richText = content.rich_text || []
                isEmpty =
                    richText.length === 0 || richText.every((rt) => !(rt.plain_text || '').trim())
            }
        }

        if (isEmpty && type !== 'divider') {
            trimmedBlocks.shift()
        } else {
            break
        }
    }

    return (
        <div className="portal-events__blocks">
            {trimmedBlocks.map((block) => {
                const type = block.type
                const content = blockContent(block)

                if (!content) return null

                // The poster already renders in the modal's own split layout —
                // image blocks from the Notion page would duplicate it (and
                // their signed URLs expire), so they're skipped.
                if (type === 'image') return null

                // Extract rich text
                const richText = content.rich_text ?? []
                const textElements = richText.map((rt, i) => {
                    const lines = (rt.plain_text || '').split('\n')
                    let text: ReactNode = lines.map((line, j) => (
                        <Fragment key={j}>
                            {line}
                            {j < lines.length - 1 && (
                                <span className="portal-events__newline-spacing" />
                            )}
                        </Fragment>
                    ))

                    if (rt.annotations?.bold) text = <strong>{text}</strong>
                    if (rt.annotations?.italic) text = <em>{text}</em>
                    if (rt.annotations?.underline) text = <u>{text}</u>
                    if (rt.annotations?.strikethrough) text = <s>{text}</s>
                    if (rt.annotations?.code) text = <code>{text}</code>

                    // Wrap with span to apply color or just for a key if there's no wrapper yet
                    let className = ''
                    if (rt.annotations?.color && rt.annotations.color !== 'default') {
                        className = `portal-events__color--${rt.annotations.color}`
                    }

                    text = (
                        <span key={i} className={className || undefined}>
                            {text}
                        </span>
                    )

                    if (rt.href)
                        return (
                            <a
                                key={i}
                                href={resolveNotionHref(rt.href)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {text}
                            </a>
                        )
                    return text
                })

                if (type === 'callout') {
                    return (
                        <div
                            key={block.id}
                            className={`portal-events__block portal-events__block--${type}`}
                        >
                            {content.icon?.emoji && <span>{content.icon.emoji}</span>}
                            <div>{textElements}</div>
                        </div>
                    )
                }

                if (textElements.length === 0 && type !== 'divider') {
                    return (
                        <div key={block.id} className="portal-events__block">
                            <br />
                        </div>
                    )
                }

                return (
                    <div
                        key={block.id}
                        className={`portal-events__block portal-events__block--${type}`}
                    >
                        {textElements}
                    </div>
                )
            })}
        </div>
    )
}

/**
 * The modal's top-right icon actions (open in Notion / copy), shared by the
 * poster and no-poster layouts. Each button carries a hover/focus tooltip
 * pill (same pattern as the sidebar toggle's "Ava menüü" tip).
 */
const ModalActions = ({
    lang,
    notionUrl,
    copied,
    onCopy,
}: {
    lang: Language
    notionUrl: string | null
    copied: boolean
    onCopy: () => void
}) => (
    <div className="portal-events__modal-actions">
        {notionUrl && (
            <a
                href={notionUrl}
                target="_blank"
                rel="noreferrer"
                className="portal-events__modal-action-btn"
                aria-label={t.portal.events.openInNotion(lang)}
            >
                <ExternalLink size={20} />
                <span className="portal-events__modal-action-tip">
                    {t.portal.events.openInNotion(lang)}
                </span>
            </a>
        )}
        <button
            className="portal-events__modal-action-btn"
            onClick={onCopy}
            aria-label={t.portal.events.copyLink(lang)}
        >
            {copied ? (
                <Check size={20} className="portal-events__text-success" />
            ) : (
                <Copy size={20} />
            )}
            <span className="portal-events__modal-action-tip">
                {t.portal.events.copyLink(lang)}
            </span>
        </button>
    </div>
)

export const EventsView = ({ lang }: { lang: Language }) => {
    const [events, setEvents] = useState<NotionEventItem[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedEvent, setSelectedEvent] = useState<NotionEventItem | null>(null)
    const [eventBlocks, setEventBlocks] = useState<NotionBlock[]>([])
    const [loadingBlocks, setLoadingBlocks] = useState(false)
    const [copied, setCopied] = useState(false)
    const [filter, setFilter] = useState<EventFilter>('upcoming')

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSelectedEvent(null)
            }
        }
        if (selectedEvent) {
            window.addEventListener('keydown', handleKeyDown)
        }
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [selectedEvent])

    useEffect(() => {
        fetch('/api/portal/events')
            .then((res) => res.json() as Promise<{ data?: NotionEventItem[] }>)
            .then((res) => {
                setEvents(res.data ?? [])
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to fetch events:', err)
                setLoading(false)
            })
    }, [])

    const now = new Date()
    const currentYear = now.getFullYear()
    const todayYmd = localYmd(now)

    const [selectedYear, setSelectedYear] = useState<number>(currentYear)

    // Years present in the data (an event "belongs" to every year its date
    // range intersects), plus the current year so the dropdown is never empty.
    const availableYears = useMemo(() => {
        const years = new Set<number>([currentYear])
        events.forEach((e) => {
            if (!e.dateStart) return
            const start = new Date(e.dateStart).getFullYear()
            const end = e.dateEnd ? new Date(e.dateEnd).getFullYear() : start
            for (let y = start; y <= end; y++) years.add(y)
        })
        return Array.from(years).sort((a, b) => b - a)
    }, [events, currentYear])

    // Past years collapse to "show everything for the year" — there's no
    // meaningful "upcoming" within a year that's already over.
    const isPastYear = selectedYear < currentYear

    const filteredEvents = useMemo(() => {
        const inYear = events.filter((e) => {
            if (!e.dateStart) return false
            const start = new Date(e.dateStart).getFullYear()
            const end = e.dateEnd ? new Date(e.dateEnd).getFullYear() : start
            return selectedYear >= start && selectedYear <= end
        })
        if (isPastYear || filter === 'all') return inYear
        // An event stays under "upcoming" until the end of its last day
        // (TASK-410) — only strictly earlier last days count as past.
        return inYear.filter((e) => !isEventPast(e.dateStart, e.dateEnd, todayYmd))
    }, [events, filter, selectedYear, isPastYear, todayYmd])

    const handleEventClick = (event: NotionEventItem) => {
        setSelectedEvent(event)
        setLoadingBlocks(true)
        setEventBlocks([])

        fetch(`/api/portal/events/${event.id}`)
            .then((res) => res.json() as Promise<{ data?: NotionBlock[] }>)
            .then((res) => {
                setEventBlocks(res.data ?? [])
                setLoadingBlocks(false)
            })
            .catch((err) => {
                console.error('Failed to fetch event blocks:', err)
                setLoadingBlocks(false)
            })
    }

    const formatDate = (start: string | null, end: string | null) => {
        if (!start) return t.portal.events.tba(lang)
        const startDate = new Date(start).toLocaleDateString(localeOf(lang), {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })
        if (!end || start === end) return startDate
        const endDate = new Date(end).toLocaleDateString(localeOf(lang), {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        })
        return `${startDate} → ${endDate}`
    }

    /**
     * Card-level date display:
     *   - Date range (multi-day): "DD.MM – DD.MM" on one line.
     *   - Single day: weekday on top, exact date underneath.
     */
    const renderCardDate = (start: string | null, end: string | null): ReactNode => {
        if (!start) return t.portal.events.tba(lang)
        if (end && start !== end) {
            return (
                <span className="portal-event-card__date-range">
                    {toDdMm(start)} – {toDdMm(end)}
                </span>
            )
        }
        const d = new Date(start)
        return (
            <>
                <span className="portal-event-card__date-weekday">{formatWeekday(d, lang)}</span>
                <span className="portal-event-card__date-day">{toDdMm(start)}</span>
            </>
        )
    }

    const handleCopyInfo = async () => {
        if (!selectedEvent) return

        const tEv = t.portal.events
        let textToCopy = `${selectedEvent.name}\n`
        if (selectedEvent.dateStart) {
            textToCopy += `${tEv.copyDateLabel(lang)}: ${formatDate(selectedEvent.dateStart, selectedEvent.dateEnd)}\n`
        }
        if (selectedEvent.place) {
            textToCopy += `${tEv.copyLocationLabel(lang)}: ${selectedEvent.place}\n`
        }
        textToCopy += `\n${tEv.copyLinkLabel(lang)}: ${window.location.href}`

        try {
            await navigator.clipboard.writeText(textToCopy)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch (err) {
            console.error('Failed to copy text', err)
        }
    }

    const headerActions = (
        <div className="portal-events__actions">
            <select
                className="portal-events__year-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                aria-label={t.portal.events.yearSelectAria(lang)}
            >
                {availableYears.map((y) => (
                    <option key={y} value={y}>
                        {y}
                    </option>
                ))}
            </select>
            {!isPastYear && (
                <Tabs
                    variant="segmented"
                    lang={lang}
                    activeKey={filter}
                    onSelect={(key) => setFilter(key as EventFilter)}
                    items={[
                        { key: 'upcoming', label: t.portal.events.filterUpcoming(lang) },
                        { key: 'all', label: t.portal.events.filterAll(lang) },
                    ]}
                />
            )}
        </div>
    )

    return (
        <PageShell>
            <PageHeader title={t.portal.events.title(lang)} actions={headerActions} />
            <div className="portal-events">
                {loading ? (
                    <div>{t.portal.events.loading(lang)}</div>
                ) : (
                    <div className="portal-events__container">
                        <div className="portal-events__grid">
                            {filteredEvents.map((event) => (
                                <div
                                    key={event.id}
                                    className="portal-event-card"
                                    onClick={() => handleEventClick(event)}
                                >
                                    <div className="portal-event-card__date-col">
                                        <div className="portal-event-card__date">
                                            {renderCardDate(event.dateStart, event.dateEnd)}
                                        </div>
                                        {event.category && (
                                            <span
                                                className="portal-event-card__chip"
                                                data-notion-color={event.categoryColor ?? 'default'}
                                            >
                                                {event.category}
                                            </span>
                                        )}
                                    </div>
                                    {event.posterUrl ? (
                                        <EventPoster
                                            src={event.posterUrl}
                                            alt={event.name}
                                            hash={event.blurHash}
                                        />
                                    ) : (
                                        <div className="portal-event-card__poster portal-event-card__poster--placeholder">
                                            {event.icon || '📅'}
                                        </div>
                                    )}
                                    <div className="portal-event-card__content">
                                        <h3 className="portal-event-card__title">
                                            {event.name || t.portal.events.untitled(lang)}
                                        </h3>
                                        {event.place && (
                                            <span
                                                className="portal-event-card__chip"
                                                data-notion-color={event.placeColor ?? 'default'}
                                            >
                                                {event.place}
                                            </span>
                                        )}
                                    </div>
                                    <SquareArrowRight
                                        className="portal-event-card__arrow"
                                        size={28}
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {selectedEvent && (
                <div
                    className="portal-events__modal-overlay"
                    onClick={() => setSelectedEvent(null)}
                >
                    <div className="portal-events__modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className={`portal-events__modal-close ${selectedEvent.posterUrl ? 'portal-events__modal-close--over-hero' : ''}`}
                            onClick={() => setSelectedEvent(null)}
                            aria-label={t.portal.events.closeEventDetails(lang)}
                        >
                            <X size={24} />
                        </button>

                        {selectedEvent.posterUrl && (
                            <div className="portal-events__modal-hero">
                                <img src={selectedEvent.posterUrl} alt={selectedEvent.name} />
                                <div className="portal-events__modal-hero-gradient" />
                            </div>
                        )}

                        <div className="portal-events__modal-body">
                            {selectedEvent.posterUrl ? (
                                <div className="portal-events__modal-split">
                                    <div className="portal-events__modal-split-poster">
                                        <img
                                            src={selectedEvent.posterUrl}
                                            alt={selectedEvent.name}
                                        />
                                    </div>
                                    <div className="portal-events__modal-split-content">
                                        <div className="portal-events__modal-header">
                                            <div className="portal-events__modal-title-row">
                                                <h2 className="portal-events__modal-title">
                                                    {selectedEvent.icon && (
                                                        <span>{selectedEvent.icon}</span>
                                                    )}
                                                    {selectedEvent.name ||
                                                        t.portal.events.untitled(lang)}
                                                </h2>
                                                <ModalActions
                                                    lang={lang}
                                                    notionUrl={selectedEvent.notionUrl}
                                                    copied={copied}
                                                    onCopy={() => void handleCopyInfo()}
                                                />
                                            </div>
                                            <div className="portal-events__modal-meta">
                                                <div className="portal-events__modal-meta-item">
                                                    <Calendar size={16} />
                                                    <span>
                                                        {formatDate(
                                                            selectedEvent.dateStart,
                                                            selectedEvent.dateEnd,
                                                        )}
                                                    </span>
                                                </div>
                                                {selectedEvent.place && (
                                                    <div className="portal-events__modal-meta-item">
                                                        <MapPin size={16} />
                                                        <span>{selectedEvent.place}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="portal-events__modal-content">
                                            {loadingBlocks ? (
                                                <div className="portal-events__modal-loading">
                                                    {t.portal.events.modalLoading(lang)}
                                                </div>
                                            ) : eventBlocks.length > 0 ? (
                                                <NotionBlockRenderer blocks={eventBlocks} />
                                            ) : (
                                                <div className="portal-events__modal-empty">
                                                    {t.portal.events.modalEmpty(lang)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="portal-events__modal-header">
                                        <div className="portal-events__modal-title-row">
                                            <h2 className="portal-events__modal-title">
                                                {selectedEvent.icon && (
                                                    <span>{selectedEvent.icon}</span>
                                                )}
                                                {selectedEvent.name ||
                                                    t.portal.events.untitled(lang)}
                                            </h2>
                                            <ModalActions
                                                lang={lang}
                                                notionUrl={selectedEvent.notionUrl}
                                                copied={copied}
                                                onCopy={() => void handleCopyInfo()}
                                            />
                                        </div>
                                        <div className="portal-events__modal-meta">
                                            <div className="portal-events__modal-meta-item">
                                                <Calendar size={16} />
                                                <span>
                                                    {formatDate(
                                                        selectedEvent.dateStart,
                                                        selectedEvent.dateEnd,
                                                    )}
                                                </span>
                                            </div>
                                            {selectedEvent.place && (
                                                <div className="portal-events__modal-meta-item">
                                                    <MapPin size={16} />
                                                    <span>{selectedEvent.place}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="portal-events__modal-content">
                                        {loadingBlocks ? (
                                            <div className="portal-events__modal-loading">
                                                {t.portal.events.modalLoading(lang)}
                                            </div>
                                        ) : eventBlocks.length > 0 ? (
                                            <NotionBlockRenderer blocks={eventBlocks} />
                                        ) : (
                                            <div className="portal-events__modal-empty">
                                                {t.portal.events.modalEmpty(lang)}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </PageShell>
    )
}
